import { useDepsChanged } from './chunk-I37XHMWY.js';
import { notifyError } from './chunk-26PMNFER.js';
import * as React3 from 'react';
import { jsx } from 'react/jsx-runtime';
import { thrownToErrorEnvelope, errorEnvelopeToErrorBody } from '@authe/fe-commons/api';
import { applyServerErrors } from '@authe/fe-commons/forms';

var Context = React3.createContext(null);
var useOptionalResourceChanges = () => React3.useContext(Context);
function ResourceChangesProvider({ hub, children, revalidateOnFocus = true, revalidateOnOnline = true }) {
  React3.useEffect(() => {
    if (!hub || typeof window === "undefined") return;
    let last = -Infinity;
    const refresh = () => {
      if (document.visibilityState === "hidden" || Date.now() - last < 1e3) return;
      last = Date.now();
      hub.revalidate();
    };
    if (revalidateOnFocus) {
      window.addEventListener("focus", refresh);
      document.addEventListener("visibilitychange", refresh);
    }
    if (revalidateOnOnline) window.addEventListener("online", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("online", refresh);
    };
  }, [hub, revalidateOnFocus, revalidateOnOnline]);
  return /* @__PURE__ */ jsx(Context.Provider, { value: hub, children });
}
function useResourceInvalidation(resources, refresh) {
  const hub = useOptionalResourceChanges();
  const resourceKey = JSON.stringify(typeof resources === "string" ? [resources] : resources);
  const latest = React3.useRef(refresh);
  React3.useEffect(() => {
    latest.current = refresh;
  });
  React3.useEffect(() => hub?.subscribeBatched((changed) => {
    if (JSON.parse(resourceKey).some((resource) => changed.has(resource))) latest.current();
  }), [hub, resourceKey]);
}
function useResourceRevision(resources) {
  const [revision, setRevision] = React3.useState(0);
  useResourceInvalidation(resources, () => setRevision((value) => value + 1));
  return revision;
}
var ApiClientContext = React3.createContext(null);
function ApiProvider({ client, children }) {
  return /* @__PURE__ */ jsx(ApiClientContext.Provider, { value: client, children });
}
function useApiClient() {
  const client = React3.useContext(ApiClientContext);
  if (!client) throw new Error("useApiClient must be used inside <ApiProvider>");
  return client;
}
function useOptionalApiClient() {
  return React3.useContext(ApiClientContext);
}
function queryData(data) {
  return { statusCode: 200, ok: true, data };
}
function emptyState(enabled) {
  return {
    data: void 0,
    envelope: null,
    error: null,
    isLoading: enabled,
    isFetching: false,
    isPlaceholderData: false
  };
}
function useQuery(fetcher, options = {}) {
  const client = useApiClient();
  const enabled = options.enabled !== false;
  const keepPreviousData = options.keepPreviousData === true;
  const fetcherRef = React3.useRef(fetcher);
  fetcherRef.current = fetcher;
  const onSuccessRef = React3.useRef(options.onSuccess);
  onSuccessRef.current = options.onSuccess;
  const onErrorRef = React3.useRef(options.onError);
  onErrorRef.current = options.onError;
  const [state, setState] = React3.useState(() => emptyState(enabled));
  const [tick, setTick] = React3.useState(0);
  const refetch = React3.useCallback(() => setTick((t) => t + 1), []);
  useResourceInvalidation(enabled ? options.resourcePolicy?.resources ?? [] : [], refetch);
  const extraDeps = options.deps ?? [];
  const priorClient = React3.useRef(client);
  const clientChanged = priorClient.current !== client;
  priorClient.current = client;
  if (useDepsChanged([client, ...extraDeps])) {
    setState(
      (s) => !clientChanged && keepPreviousData && s.data !== void 0 ? { ...s, error: null, isLoading: false, isFetching: enabled, isPlaceholderData: true } : { ...emptyState(enabled), isFetching: enabled }
    );
  }
  React3.useEffect(() => {
    if (!enabled) return;
    let active = true;
    const ctrl = new AbortController();
    setState((s) => ({ ...s, isFetching: true, isLoading: s.data === void 0, error: null }));
    fetcherRef.current(client, ctrl.signal).then(
      (res) => {
        if (!active) return;
        if (res.ok) {
          setState({
            data: res.data,
            envelope: res,
            error: null,
            isLoading: false,
            isFetching: false,
            isPlaceholderData: false
          });
          onSuccessRef.current?.(res.data, res);
        } else {
          setState((s) => ({ ...s, error: res, isLoading: false, isFetching: false }));
          onErrorRef.current?.(res);
        }
      },
      (err) => {
        if (!active) return;
        if (ctrl.signal.aborted) {
          setState((s) => ({ ...s, isFetching: false }));
          return;
        }
        const envelope = thrownToErrorEnvelope(err);
        setState((s) => ({ ...s, error: envelope, isLoading: false, isFetching: false }));
        onErrorRef.current?.(envelope);
      }
    );
    return () => {
      active = false;
      ctrl.abort();
    };
  }, [client, enabled, tick, ...extraDeps]);
  return { ...state, refetch };
}
function useMutation(fn, options = {}) {
  const client = useApiClient();
  const optionsRef = React3.useRef(options);
  optionsRef.current = options;
  const [data, setData] = React3.useState(void 0);
  const [error, setError] = React3.useState(null);
  const [isLoading, setIsLoading] = React3.useState(false);
  const reset = React3.useCallback(() => {
    setData(void 0);
    setError(null);
    setIsLoading(false);
  }, []);
  const mutate = React3.useCallback(
    async (args) => {
      const opts = optionsRef.current;
      setIsLoading(true);
      setError(null);
      let res;
      try {
        res = await fn(client, args);
      } catch (err) {
        res = thrownToErrorEnvelope(err);
      }
      if (res.ok) {
        setData(res.data);
        setError(null);
        opts.onSuccess?.(res.data, res, args);
      } else {
        setError(res);
        opts.onError?.(res, args);
        if (opts.setFormError) applyServerErrors(errorEnvelopeToErrorBody(res), opts.setFormError);
        if (opts.notifyOnError) {
          notifyError(res, typeof opts.notifyOnError === "object" ? opts.notifyOnError : {});
        }
      }
      setIsLoading(false);
      return res;
    },
    [client, fn]
  );
  return { mutate, data, error, isLoading, reset };
}

export { ApiProvider, ResourceChangesProvider, queryData, useApiClient, useMutation, useOptionalApiClient, useOptionalResourceChanges, useQuery, useResourceInvalidation, useResourceRevision };
//# sourceMappingURL=chunk-CN2R5HL5.js.map
//# sourceMappingURL=chunk-CN2R5HL5.js.map